import { useState, useEffect, useRef } from 'react';
import { useAuthStore } from '../../auth/store';
import { usernameFromToken } from '../../auth/tabRegistry';
import { api } from '../../api/client';
import { useNotify } from '../../components/notifications';
import {
  LoaderSymbol, LOADER_SYMBOLS, PROGRESS_BAR_STYLES,
  setLoaderSymbol, setLoaderColor, setProgressStyle, setProgressColor, useLoadingStore,
} from '../../components/loading';
import { useSdmsRollbackUi, setSdmsRollbackUi, getSdmsRollbackUi } from '../../utils/sdmsRollbackUi';
import { FormTitle } from '../../components/FormTitle';
import { AppToolbar, EditGateActions } from '../../components/AppToolbar';
import { useEditGate } from '../../components/confirm';
import { useFetchMs } from '../../hooks/useFetchMs';
import '../backlog/backlog.css';

/** Preset loader colours offered alongside the custom picker. */
/* token-exempt: user-selectable swatch data (the picker's palette values, not chrome styling) */
const PRESET_LOADER_COLORS = ['#e8670a', '#2563eb', '#0F6E56', '#9F1239', '#7C3AED', '#1a1a2e'];

/** Global login modes (item 1 — Login Options).  Working: standard ·
 *  twofactor · mfa.  sso-saml / ad are selectable-looking but disabled —
 *  the API rejects them with "requires IdP setup" until an IdP exists. */
type LoginMode = 'standard' | 'twofactor' | 'mfa' | 'sso-saml' | 'ad';
const LOGIN_MODES: Array<{
  value: LoginMode; label: string; desc: string; disabled?: boolean;
}> = [
  { value: 'standard',  label: 'Login',            desc: 'Username + password' },
  { value: 'twofactor', label: 'Two-Factor',       desc: 'Password + one-time code by email — forced for every user' },
  { value: 'mfa',       label: 'MFA',              desc: 'Password + authenticator app (TOTP) — un-enrolled users are taken to enrollment at sign-in' },
  { value: 'sso-saml',  label: 'SSO (SAML)',       desc: 'Requires IdP setup — coming soon', disabled: true },
  { value: 'ad',        label: 'Active Directory', desc: 'Requires IdP setup — coming soon', disabled: true },
];

/**
 * Settings → Default Settings
 *
 * Port of GoLive's "Default Settings" configuration form (global lab
 * workflow toggles).  Items 1 (Login Options), 5-9 + Skip 8/9 (Data Review
 * workflow — SDMS / SDMS Result Validation / SDMS Result Approval / Result
 * Validation / Result Approval), 13 (No Self-Review), 20-22 (Loading
 * Indicator / Progress Bar / Report Signing) are real, DB-backed, and save.
 * Items 2-4, 10-12, 14-19 are still decorative — Save / live DB binding for
 * those ships in a later step.
 *
 * Access — **service operator only** (SERVICE_EXCLUSIVE in navPermissions).
 * Nav-hidden for everyone else; re-checked at render time so a direct URL hit
 * also bounces.  Matches the gating Form Config uses.
 *
 * Layout (2026-05-31, after user feedback "don't want this extra things"):
 *   • Flat numbered rows — no section headings, no per-row hint subtext.
 *   • Yes/On → green, No/Off → red.
 *   • Scrollbar inside the card; the form fills the full card width so the
 *     scrollbar sits at the far right edge, not in a narrow centred column.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Service-only guard
// ─────────────────────────────────────────────────────────────────────────────
function ServiceOnlyBlock() {
  return (
    <div className="bl-page">
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span>
            <i className="ti ti-chevron-right" />
            <span>Settings</span>
            <i className="ti ti-chevron-right" />
            <span className="dp-bc-active">Default Settings</span>
          </div>
          <div className="dp-title-row"><FormTitle pageId="defaultsettings" /></div>
        </div>
      </div>
      <div className="bl-grid-area">
        <div className="bl-card bl-card-flex" style={{ padding: 32, alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ textAlign: 'center', maxWidth: 480, color: 'var(--ai-muted)' }}>
            <i className="ti ti-shield-lock" style={{ fontSize: 36, color: 'var(--ai-muted)' }} />
            <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--ai-ink)', margin: '12px 0 6px' }}>
              Service-only page
            </h2>
            <p style={{ fontSize: 13, lineHeight: 1.55, margin: 0 }}>
              Default Settings is reserved for the built-in <code style={{
                background: 'var(--ai-orange-tint)', border: '1px solid var(--ai-required-line)', padding: '1px 6px',
                borderRadius: 4, color: 'var(--ai-orange-hover)', fontFamily: 'inherit',
              }}>service</code> operator account.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Atoms
// ─────────────────────────────────────────────────────────────────────────────
function Choice({ value, opts }: { value: string; opts: [string, string] }) {
  const colourFor = (label: string): string => {
    const v = label.toLowerCase();
    if (v === 'yes' || v === 'on')  return 'var(--ai-green)';
    if (v === 'no'  || v === 'off') return 'var(--ai-red)';
    return 'var(--ai-ink)';
  };
  return (
    <span style={{ display: 'inline-flex', gap: 26, alignItems: 'center', minWidth: 130 }}>
      {opts.map((o) => {
        const active = value === o;
        const colour = active ? colourFor(o) : 'var(--ai-muted)';
        return (
          <label key={o} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            color: colour, fontWeight: active ? 700 : 400, cursor: 'default',
            fontSize: 13,
          }}>
            <input type="radio" checked={active} readOnly tabIndex={-1}
              style={{ accentColor: colour, pointerEvents: 'none' }} />
            {o}
          </label>
        );
      })}
    </span>
  );
}

function Box({ value, placeholder, width = 220 }: { value?: string; placeholder?: string; width?: number }) {
  return (
    <input
      type="text"
      value={value ?? ''}
      placeholder={placeholder}
      readOnly disabled
      style={{
        width, padding: '6px 10px', border: '1px solid var(--ai-line)', borderRadius: 5,
        background: 'var(--ai-readonly-bg)', color: 'var(--ai-ink)', fontSize: 13,
      }}
    />
  );
}

/** Interactive On/Off radio pair (Report Signing panel). */
function OnOff({ value, disabled, onPick }: { value: boolean; disabled?: boolean; onPick: (v: boolean) => void }) {
  const items: Array<[string, boolean]> = [['On', true], ['Off', false]];
  return (
    <span style={{ display: 'inline-flex', gap: 22, alignItems: 'center' }}>
      {items.map(([label, v]) => {
        const active = value === v;
        const colour = active ? (v ? 'var(--ai-green)' : 'var(--ai-red)') : 'var(--ai-muted)';
        return (
          <label key={label} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: colour, fontWeight: active ? 700 : 400, cursor: disabled ? 'wait' : 'pointer', fontSize: 13 }}>
            <input type="radio" checked={active} disabled={disabled} onChange={() => onPick(v)} style={{ accentColor: colour }} />
            {label}
          </label>
        );
      })}
    </span>
  );
}

/** Interactive Yes/No radio pair (Data Review workflow settings, items 5-9). */
function YesNo({ value, disabled, onPick }: { value: boolean; disabled?: boolean; onPick: (v: boolean) => void }) {
  const items: Array<[string, boolean]> = [['Yes', true], ['No', false]];
  return (
    <span style={{ display: 'inline-flex', gap: 26, alignItems: 'center', minWidth: 130 }}>
      {items.map(([label, v]) => {
        const active = value === v;
        const colour = active ? (v ? 'var(--ai-green)' : 'var(--ai-red)') : 'var(--ai-muted)';
        return (
          <label key={label} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: colour, fontWeight: active ? 700 : 400, cursor: disabled ? 'wait' : 'pointer', fontSize: 13 }}>
            <input type="radio" checked={active} disabled={disabled} onChange={() => onPick(v)} style={{ accentColor: colour }} />
            {label}
          </label>
        );
      })}
    </span>
  );
}

/** Interactive checkbox (Skip 8 / Skip 9). */
function SkipBox({ label, checked, disabled, onPick }: { label: string; checked: boolean; disabled?: boolean; onPick: (v: boolean) => void }) {
  return (
    <label style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13,
      color: checked ? 'var(--ai-ink)' : 'var(--ai-muted)', fontWeight: checked ? 600 : 400,
      cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1,
    }}>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onPick(e.target.checked)}
        style={{ accentColor: 'var(--brand-accent, var(--ai-orange))' }} />
      {label}
    </label>
  );
}

/** Numbered row: "N. Label" on the left, controls on the right. `n` is optional
 *  so a NEW AI-only setting (e.g. Multi-Tab) can sit inside a group without
 *  renumbering the ALPACA-canonical items around it — the "Skip 8 / Skip 9"
 *  checkboxes reference items 8 & 9 by number, so those must not shift. */
function Row({ n, label, children }: { n?: number | string; label: string; children: React.ReactNode }) {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '260px 1fr', columnGap: 24,
      alignItems: 'center', padding: '9px 4px', borderBottom: '1px solid var(--ai-line-soft)',
    }}>
      <div style={{ color: 'var(--ai-ink)', fontSize: 13.5, fontWeight: 500 }}>
        {n !== undefined && <span style={{ color: 'var(--ai-muted)', marginRight: 4 }}>{n}.</span>}{label}:
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
        {children}
      </div>
    </div>
  );
}

/** Editable caption field shown beside items 6-9 (owner request 2026-10-15, legacy
 *  lblSDMSValidate / lblSDMSApprove / lblLIMSValidate / lblLIMSApprove). Module scope so
 *  it keeps a stable identity across renders (no focus loss while typing). */
// The step-mapping caption beside items #6-#9 (legacy lblSDMSValidate /
// lblSDMSApprove / lblLIMSValidate / lblLIMSApprove). ALPACA does NOT let the user
// type these — the controller COMPUTES them from the SDMS / Validation / Approval
// Yes/No combination and shows them read-only (owner decision 2026-10-15, confirmed
// vs legacy). So this is a read-only display, not an input. Grey background = the
// standard read-only cue; empty value renders an empty box, exactly like legacy.
function CaptionLabel({ value }: { value: string }) {
  return (
    <input type="text" value={value} readOnly tabIndex={-1} aria-label="Step mapping"
      style={{ height: 26, fontSize: 13, padding: '0 8px', border: '1px solid var(--ai-line)', borderRadius: 4, width: 200, boxSizing: 'border-box', background: '#f6f7f9', color: 'var(--ai-ink)', cursor: 'default' }} />
  );
}

/** The read-only step-mapping captions beside items 6-9, computed from the SDMS /
 *  Validation / Approval Yes/No choices EXACTLY as ALPACA's
 *  ResultEntryDefaultSettingsController.ObjectSpace_ObjectChanged does (ported
 *  verbatim, lines 355-467). Legacy computes AND persists them, so the Save path
 *  writes these same values. l2 = SDMS Result Validation (Leve2DataReview), l3 =
 *  SDMS Result Approval (Leve3DataReview), rv = Result Validation, ra = Result
 *  Approval. */
function computeDataReviewLabels(
  sdms: boolean, l2: boolean, l3: boolean, rv: boolean, ra: boolean,
): { sdmsValidate: string; sdmsApprove: string; limsValidate: string; limsApprove: string } {
  const r = (sdmsValidate: string, sdmsApprove: string, limsValidate: string, limsApprove: string) =>
    ({ sdmsValidate, sdmsApprove, limsValidate, limsApprove });
  if (sdms) {
    if (l2 && !l3 && !rv && !ra) return r('6 = 7 = 8 = 9', '6 = 7 = 8 = 9', '6 = 7 = 8 = 9', '6 = 7 = 8 = 9');
    if (l2 && !l3 && !rv && ra)  return r('6 = 7', '7 = 6', '8 = 9', '9 = 8');
    if (l2 && !l3 && rv && !ra)  return r('6 = 7', '7 = 6', '8 = 9', '9 = 8');
    if (l2 && !l3 && rv && ra)   return r('6 = 7', '7 = 6', '8 = 8', '9 = 9');
    if (l2 && l3 && !rv && !ra)  return r('6 = 8', '7 = 9', '8 = 6', '9 = 7');
    if (l2 && l3 && !rv && ra)   return r('6 = 6', '7 = 7', '8 = 9', '9 = 8');
    if (l2 && l3 && rv && !ra)   return r('6 = 6', '7 = 7', '8 = 9', '9 = 8');
    if (l2 && l3 && rv && ra)    return r('6 = 6', '7 = 7', '8 = 8', '9 = 9');
    return r('', '', '', '');
  }
  if (!rv && ra) return r('', '', '8 = 9', '9 = 9');
  if (rv && !ra) return r('', '', '8 = 9', '9 = 8');
  if (rv && ra)  return r('', '', '8 = 8', '9 = 9');
  return r('', '', '', '');
}

// ─────────────────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────────────────
export default function DefaultSettingsPage() {
  // Service-only guard.  Direct URL hit by a non-service user → blocked.
  // Compares the LOGIN name, not the display name. `user.name` is the linked
  // Employee.DisplayName — "Service User" for this account — so comparing it to
  // 'service' never matched and the real service operator was locked out of its
  // own page (user report 2026-09-24). The JWT `name` claim IS the UserName and
  // is what the API's own IsServiceUser() reads, so it is the one value
  // guaranteed to agree with the server; `user.userName` covers a session minted
  // before that field existed.
  const user  = useAuthStore(s => s.user);
  const token = useAuthStore(s => s.token);
  const login = (usernameFromToken(token) || user?.userName || '').toLowerCase().trim();
  if (login !== 'service') return <ServiceOnlyBlock />;

  // ── View-then-Edit gate (BO UI Standard v2) — the SHARED config-panel flow ─
  // The page opens READ-ONLY; the toolbar Edit button unlocks the controls;
  // Save persists EVERYTHING in one audited batch (shared useSaveFlow); Cancel
  // reverts to the pre-edit snapshot.  No control saves on change any more —
  // "after Save only the data is saved" (user request 2026-07-07).
  const notify = useNotify();
  const gate = useEditGate();
  const ro = gate.readOnly;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const loadMs = useFetchMs(loading);

  // Loader / progress live in the shared loading store (applied app-wide). In
  // edit mode a pick updates the store for LIVE PREVIEW only; nothing is
  // persisted until Save.  Cancel reverts the store to the pre-edit snapshot.
  const symbol = useLoadingStore((s) => s.symbol);
  const color = useLoadingStore((s) => s.color);
  const progressStyle = useLoadingStore((s) => s.progressStyle);
  const progressColor = useLoadingStore((s) => s.progressColor);

  // ── Report Signing (ADR-0009) + Login Options — local editable state ───────
  const [signEnabled, setSignEnabled] = useState(false);
  const [approvalRequired, setApprovalRequired] = useState(false);
  const [secondFactor, setSecondFactor] = useState('Mfa');
  const [loginMode, setLoginModeState] = useState<LoginMode>('standard');

  // ── Data Review workflow config (items 5-9 + Skip 8/9 + 13) — local editable
  // state, cross-field-reconciled LIVE (mirrors ALPACA
  // ResultEntryDefaultSettingsController.ObjectSpace_ObjectChanged); persisted
  // + cascaded into dbo.DefaultSetting on Save (mirrors SaveAction_Executing).
  // This is the SAME dbo.ResultEntryDefaultSettings row
  // AnalysisWriter.ApplySdmsSampleStatusProgressionAsync reads, so a Save here
  // takes effect immediately for both ALPACA and Alpaca Intelligence.
  const [sdms, setSdmsState] = useState(false);
  const [sdmsResultValidation, setSdmsResultValidationState] = useState(false);   // legacy Leve2DataReview
  const [sdmsResultApproval, setSdmsResultApprovalState] = useState(false);       // legacy Leve3DataReview
  const [resultValidation, setResultValidationState] = useState(false);
  const [resultApproval, setResultApprovalState] = useState(false);
  const [skipResultValidation, setSkipResultValidationState] = useState(false);
  const [skipResultApproval, setSkipResultApprovalState] = useState(false);
  const [noSelfReview, setNoSelfReviewState] = useState(false);
  // SDMS Result Validation form: split category tabs (Yes) vs single legacy
  // "Batch Results" tab (No, default). AI-only (AI_AppSettings 'SdmsMultiTab').
  const [multiTab, setMultiTabState] = useState(false);
  // SDMS Result Validation Rollback UI (AI_AppSettings 'SdmsRollbackUi') —
  // 'classic' (reason-only popup) vs 'enhanced' (typed roll-back window). Read
  // from the shared app-wide store so the switch previews live (like the loader).
  const rollbackUi = useSdmsRollbackUi();

  // ── Default Settings items 2/3/10/12/14-19 (owner request 2026-10-15): the
  // remaining dbo.ResultEntryDefaultSettings toggles, previously display-only
  // placeholders, now editable. Persisted with the rest via data-review-settings. ──
  const [suboutResultValidation, setSuboutResultValidationState] = useState(false);     // #2 SuboutLeve2DataReview
  const [suboutResultApproval, setSuboutResultApprovalState] = useState(false);         // #3 SuboutLeve3DataReview
  const [electronicSignature, setElectronicSignatureState] = useState(false);           // #10 ElectronicSignature (On/Off)
  const [dataPackage, setDataPackageState] = useState(false);                           // #12 DataPackageValue
  const [fieldDataValidation, setFieldDataValidationState] = useState(false);           // #14 FieldDataValidation
  const [fieldDataApproval, setFieldDataApprovalState] = useState(false);               // #15 FieldDataApproval
  const [manualReagentEntrySdms, setManualReagentEntrySdmsState] = useState(false);     // #16 ReagentEntrySDMS
  const [sampleReceiptNotification, setSampleReceiptNotificationState] = useState(false); // #17 SampleReceiptNotification
  const [suboutTracking, setSuboutTrackingState] = useState(false);                     // #18 SuboutTracking
  const [reporting, setReportingState] = useState(false);                               // #19 Reporting
  // Items 20/21 + QC Batch + Prep Batch (owner request 2026-10-15).
  const [multipleTabSupport, setMultipleTabSupportState] = useState(false);             // #20 MultipleTabSupport (On/Off)
  const [selfSubmit, setSelfSubmitState] = useState(false);                             // #21 SelfSubmit (On/Off)
  const [qcTemperature, setQcTemperatureState] = useState(false);                       // QC Batch Temperature
  const [qcHumidity, setQcHumidityState] = useState(false);                             // QC Batch Humidity
  const [qcScanQrCode, setQcScanQrCodeState] = useState(false);                         // QC Batch Scan QR Code
  const [applyRoundingOnSave, setApplyRoundingOnSaveState] = useState(false);           // QC Batch Apply Rounding On Save
  const [prepTemperature, setPrepTemperatureState] = useState(false);                   // Prep Batch Temperature
  const [prepHumidity, setPrepHumidityState] = useState(false);                         // Prep Batch Humidity
  const [prepScanQrCode, setPrepScanQrCodeState] = useState(false);                     // Prep Batch Scan QR Code
  const [analysisListView, setAnalysisListViewState] = useState<'QCBatch' | 'JobID'>('QCBatch'); // Prep Batch Analysis List View
  const [invoiceSeqCode, setInvoiceSeqCodeState] = useState('');                        // Prep Batch Invoice Seq Code (text)
  // Read-only step-mapping captions beside items 6-9 (lblSDMSValidate / lblSDMSApprove
  // / lblLIMSValidate / lblLIMSApprove). ⚠️ These show the STORED value on load and are
  // RECOMPUTED only when a toggle changes — EXACTLY like ALPACA, whose
  // ObjectSpace_ObjectChanged recomputes them on a property change but leaves the stored
  // value untouched on open (owner report 2026-10-15: the AI form showed the recomputed
  // mapping "6 = 7 = 8 = 9" while the legacy form showed the stored "6 = 6 / 7 = 7 /
  // 8 = 9 / 9 = 8"). So they are STATE loaded from the DB — not a pure derived memo,
  // which would overwrite the stored value on load — and the toggle pick handlers call
  // applyDataReviewLabels to recompute; load / Cancel keep the stored / snapshot values.
  const [sdmsValidateLabel, setSdmsValidateLabelState] = useState('');
  const [sdmsApproveLabel, setSdmsApproveLabelState] = useState('');
  const [limsValidateLabel, setLimsValidateLabelState] = useState('');
  const [limsApproveLabel, setLimsApproveLabelState] = useState('');
  const applyDataReviewLabels = (s: boolean, l2: boolean, l3: boolean, rv: boolean, ra: boolean) => {
    const c = computeDataReviewLabels(s, l2, l3, rv, ra);
    setSdmsValidateLabelState(c.sdmsValidate);
    setSdmsApproveLabelState(c.sdmsApprove);
    setLimsValidateLabelState(c.limsValidate);
    setLimsApproveLabelState(c.limsApprove);
  };

  // Snapshot of every editable value taken when Edit is pressed — powers Cancel.
  const snapshotRef = useRef<null | {
    loginMode: LoginMode; symbol: string; color: string;
    progressStyle: string; progressColor: string;
    signEnabled: boolean; approvalRequired: boolean; secondFactor: string;
    sdms: boolean; sdmsResultValidation: boolean; sdmsResultApproval: boolean;
    resultValidation: boolean; resultApproval: boolean;
    skipResultValidation: boolean; skipResultApproval: boolean; noSelfReview: boolean;
    multiTab: boolean; rollbackUi: 'classic' | 'enhanced';
    suboutResultValidation: boolean; suboutResultApproval: boolean; electronicSignature: boolean;
    dataPackage: boolean; fieldDataValidation: boolean; fieldDataApproval: boolean;
    manualReagentEntrySdms: boolean; sampleReceiptNotification: boolean; suboutTracking: boolean; reporting: boolean;
    multipleTabSupport: boolean; selfSubmit: boolean; qcTemperature: boolean; qcHumidity: boolean;
    qcScanQrCode: boolean; applyRoundingOnSave: boolean; prepTemperature: boolean; prepHumidity: boolean;
    prepScanQrCode: boolean; analysisListView: 'QCBatch' | 'JobID'; invoiceSeqCode: string;
    sdmsValidateLabel: string; sdmsApproveLabel: string; limsValidateLabel: string; limsApproveLabel: string;
  }>(null);
  useEffect(() => {
    snapshotRef.current = gate.editing
      ? {
          loginMode, symbol, color, progressStyle, progressColor, signEnabled, approvalRequired, secondFactor,
          sdms, sdmsResultValidation, sdmsResultApproval, resultValidation, resultApproval,
          skipResultValidation, skipResultApproval, noSelfReview, multiTab, rollbackUi: getSdmsRollbackUi(),
          suboutResultValidation, suboutResultApproval, electronicSignature, dataPackage,
          fieldDataValidation, fieldDataApproval, manualReagentEntrySdms, sampleReceiptNotification,
          suboutTracking, reporting,
          multipleTabSupport, selfSubmit, qcTemperature, qcHumidity, qcScanQrCode, applyRoundingOnSave,
          prepTemperature, prepHumidity, prepScanQrCode, analysisListView, invoiceSeqCode,
          sdmsValidateLabel, sdmsApproveLabel, limsValidateLabel, limsApproveLabel,
        }
      : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gate.editing]);

  // Edit-mode pickers — LOCAL ONLY (live preview for loader/progress, plain
  // state for login/signing).  Guarded so a click while read-only is a no-op.
  const pickSymbol = (sym: (typeof LOADER_SYMBOLS)[number]) => { if (!ro) setLoaderSymbol(sym); };
  const pickColor = (c: string) => { if (!ro && c) setLoaderColor(c); };
  const pickStyle = (st: (typeof PROGRESS_BAR_STYLES)[number]) => { if (!ro) setProgressStyle(st); };
  const pickProgressColor = (c: string) => { if (!ro && c) setProgressColor(c); };
  const pickLoginMode = (m: LoginMode) => { if (!ro) setLoginModeState(m); };

  // Data Review pickers — cross-field reconciliation mirrors ALPACA's
  // ObjectSpace_ObjectChanged: SDMS=No forces SDMS Result Validation=No + both
  // Skip flags off; SDMS Result Validation=No forces SDMS Result Approval=No. Each
  // also RECOMPUTES the step-mapping captions from the NEW combo (applyDataReviewLabels)
  // — the other half of ObjectChanged — using the explicit next values (setState is
  // async). Only here, so a load / Cancel keeps the stored / snapshot captions.
  const pickSdms = (v: boolean) => {
    if (ro) return;
    setSdmsState(v);
    if (!v) {
      setSdmsResultValidationState(false);
      setSdmsResultApprovalState(false);
      setSkipResultValidationState(false);
      setSkipResultApprovalState(false);
    }
    applyDataReviewLabels(v, v && sdmsResultValidation, v && sdmsResultApproval, resultValidation, resultApproval);
  };
  const pickSdmsResultValidation = (v: boolean) => {
    if (ro) return;
    setSdmsResultValidationState(v);
    if (!v) setSdmsResultApprovalState(false);
    applyDataReviewLabels(sdms, v, v && sdmsResultApproval, resultValidation, resultApproval);
  };
  const pickSdmsResultApproval = (v: boolean) => {
    if (ro) return;
    setSdmsResultApprovalState(v);
    applyDataReviewLabels(sdms, sdmsResultValidation, v, resultValidation, resultApproval);
  };
  const pickResultValidation = (v: boolean) => {
    if (ro) return;
    setResultValidationState(v);
    applyDataReviewLabels(sdms, sdmsResultValidation, sdmsResultApproval, v, resultApproval);
  };
  const pickResultApproval = (v: boolean) => {
    if (ro) return;
    setResultApprovalState(v);
    applyDataReviewLabels(sdms, sdmsResultValidation, sdmsResultApproval, resultValidation, v);
  };
  const pickSkipResultValidation = (v: boolean) => { if (!ro && sdms) setSkipResultValidationState(v); };
  const pickSkipResultApproval = (v: boolean) => { if (!ro && sdms) setSkipResultApprovalState(v); };
  const pickNoSelfReview = (v: boolean) => { if (!ro) setNoSelfReviewState(v); };
  const pickMultiTab = (v: boolean) => { if (!ro) setMultiTabState(v); };
  // Items 2/3/10/12/14-19. Subout Result Approval is forced off + disabled when Subout
  // Result Validation = No (legacy ResultEntryDefaultSettings.cs:76 Appearance rule).
  const pickSuboutResultValidation = (v: boolean) => { if (!ro) { setSuboutResultValidationState(v); if (!v) setSuboutResultApprovalState(false); } };
  const pickSuboutResultApproval = (v: boolean) => { if (!ro && suboutResultValidation) setSuboutResultApprovalState(v); };
  const pickElectronicSignature = (v: boolean) => { if (!ro) setElectronicSignatureState(v); };
  const pickDataPackage = (v: boolean) => { if (!ro) setDataPackageState(v); };
  const pickFieldDataValidation = (v: boolean) => { if (!ro) setFieldDataValidationState(v); };
  const pickFieldDataApproval = (v: boolean) => { if (!ro) setFieldDataApprovalState(v); };
  const pickManualReagentEntrySdms = (v: boolean) => { if (!ro) setManualReagentEntrySdmsState(v); };
  const pickSampleReceiptNotification = (v: boolean) => { if (!ro) setSampleReceiptNotificationState(v); };
  const pickSuboutTracking = (v: boolean) => { if (!ro) setSuboutTrackingState(v); };
  const pickReporting = (v: boolean) => { if (!ro) setReportingState(v); };
  // Items 20/21 + QC/Prep Batch.
  const pickMultipleTabSupport = (v: boolean) => { if (!ro) setMultipleTabSupportState(v); };
  const pickSelfSubmit = (v: boolean) => { if (!ro) setSelfSubmitState(v); };
  const pickQcTemperature = (v: boolean) => { if (!ro) setQcTemperatureState(v); };
  const pickQcHumidity = (v: boolean) => { if (!ro) setQcHumidityState(v); };
  const pickQcScanQrCode = (v: boolean) => { if (!ro) setQcScanQrCodeState(v); };
  const pickApplyRoundingOnSave = (v: boolean) => { if (!ro) setApplyRoundingOnSaveState(v); };
  const pickPrepTemperature = (v: boolean) => { if (!ro) setPrepTemperatureState(v); };
  const pickPrepHumidity = (v: boolean) => { if (!ro) setPrepHumidityState(v); };
  const pickPrepScanQrCode = (v: boolean) => { if (!ro) setPrepScanQrCodeState(v); };
  const pickAnalysisListView = (v: 'QCBatch' | 'JobID') => { if (!ro) setAnalysisListViewState(v); };
  const pickRollbackUi = (v: 'classic' | 'enhanced') => { if (!ro) setSdmsRollbackUi(v); };

  // Animated demo — fills 0→100% on a loop so the chosen style + colour
  // (stripes scrolling, pulse, glow, the light→bright gradient) is visible.
  const [demoPct, setDemoPct] = useState(20);
  useEffect(() => {
    const id = window.setInterval(() => setDemoPct((p) => (p >= 100 ? 0 : p + 3)), 80);
    return () => window.clearInterval(id);
  }, []);

  /** Load the live config (login mode + report-signing) — reused by the
   *  toolbar Refresh.  Tracks `loading` so the shared FormTitle load-time chip
   *  and the Refresh spinner reflect it. */
  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/v1/app-config');
      const d = (res.data as { data?: {
        reportSignEnabled?: boolean; reportApprovalRequired?: boolean; reportSignSecondFactor?: string; loginMode?: LoginMode;
        sdmsRollbackUi?: string;
        dataReviewSettings?: {
          sdms?: boolean; sdmsResultValidation?: boolean; sdmsResultApproval?: boolean;
          resultValidation?: boolean; resultApproval?: boolean;
          skipResultValidation?: boolean; skipResultApproval?: boolean; noSelfReview?: boolean;
          multiTab?: boolean;
          suboutResultValidation?: boolean; suboutResultApproval?: boolean; electronicSignature?: boolean;
          dataPackage?: boolean; fieldDataValidation?: boolean; fieldDataApproval?: boolean;
          manualReagentEntrySdms?: boolean; sampleReceiptNotification?: boolean; suboutTracking?: boolean; reporting?: boolean;
          multipleTabSupport?: boolean; selfSubmit?: boolean; qcTemperature?: boolean; qcHumidity?: boolean;
          qcScanQrCode?: boolean; applyRoundingOnSave?: boolean; prepTemperature?: boolean; prepHumidity?: boolean;
          prepScanQrCode?: boolean; analysisListView?: string; invoiceSeqCode?: string | null;
          sdmsValidateLabel?: string | null; sdmsApproveLabel?: string | null;
          limsValidateLabel?: string | null; limsApproveLabel?: string | null;
        };
      } }).data;
      setSignEnabled(d?.reportSignEnabled === true);
      setApprovalRequired(d?.reportApprovalRequired === true);
      setSecondFactor(d?.reportSignSecondFactor ?? 'Mfa');
      setLoginModeState(d?.loginMode ?? 'standard');
      setSdmsRollbackUi(d?.sdmsRollbackUi ?? 'classic');
      const dr = d?.dataReviewSettings;
      setSdmsState(dr?.sdms === true);
      setSdmsResultValidationState(dr?.sdmsResultValidation === true);
      setSdmsResultApprovalState(dr?.sdmsResultApproval === true);
      setResultValidationState(dr?.resultValidation === true);
      setResultApprovalState(dr?.resultApproval === true);
      setSkipResultValidationState(dr?.skipResultValidation === true);
      setSkipResultApprovalState(dr?.skipResultApproval === true);
      setNoSelfReviewState(dr?.noSelfReview === true);
      setMultiTabState(dr?.multiTab === true);
      setSuboutResultValidationState(dr?.suboutResultValidation === true);
      setSuboutResultApprovalState(dr?.suboutResultApproval === true);
      setElectronicSignatureState(dr?.electronicSignature === true);
      setDataPackageState(dr?.dataPackage === true);
      setFieldDataValidationState(dr?.fieldDataValidation === true);
      setFieldDataApprovalState(dr?.fieldDataApproval === true);
      setManualReagentEntrySdmsState(dr?.manualReagentEntrySdms === true);
      setSampleReceiptNotificationState(dr?.sampleReceiptNotification === true);
      setSuboutTrackingState(dr?.suboutTracking === true);
      setReportingState(dr?.reporting === true);
      setMultipleTabSupportState(dr?.multipleTabSupport === true);
      setSelfSubmitState(dr?.selfSubmit === true);
      setQcTemperatureState(dr?.qcTemperature === true);
      setQcHumidityState(dr?.qcHumidity === true);
      setQcScanQrCodeState(dr?.qcScanQrCode === true);
      setApplyRoundingOnSaveState(dr?.applyRoundingOnSave === true);
      setPrepTemperatureState(dr?.prepTemperature === true);
      setPrepHumidityState(dr?.prepHumidity === true);
      setPrepScanQrCodeState(dr?.prepScanQrCode === true);
      setAnalysisListViewState(dr?.analysisListView === 'JobID' ? 'JobID' : 'QCBatch');
      setInvoiceSeqCodeState(dr?.invoiceSeqCode ?? '');
      // Load the STORED step-mapping captions as-is (legacy shows them unchanged on open
      // and recomputes only on a toggle change — see applyDataReviewLabels). They may be
      // stale vs the current combo; that is intentional parity with ALPACA.
      setSdmsValidateLabelState(dr?.sdmsValidateLabel ?? '');
      setSdmsApproveLabelState(dr?.sdmsApproveLabel ?? '');
      setLimsValidateLabelState(dr?.limsValidateLabel ?? '');
      setLimsApproveLabelState(dr?.limsApproveLabel ?? '');
    } catch {
      notify.error('Could not load default settings.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { void load(); }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  /** Persist EVERYTHING in one audited batch (the shared Save). E-signature
   *  implies Approval (the 2nd signature), so force Approval on when e-sign is. */
  const persistAll = async () => {
    const approval = signEnabled ? true : approvalRequired;
    await api.put('/api/v1/app-config/login-mode', { mode: loginMode });
    await api.put('/api/v1/app-config/sdms-rollback-ui', { mode: getSdmsRollbackUi() });
    await api.put('/api/v1/app-config/loader-symbol', { symbol });
    await api.put('/api/v1/app-config/loader-color', { color });
    await api.put('/api/v1/app-config/progress-bar-style', { style: progressStyle });
    await api.put('/api/v1/app-config/progress-bar-color', { color: progressColor });
    await api.put('/api/v1/app-config/report-sign-enabled', { enabled: signEnabled });
    await api.put('/api/v1/app-config/report-approval-required', { enabled: approval });
    await api.put('/api/v1/app-config/report-sign-second-factor', { factor: secondFactor });
    await api.put('/api/v1/app-config/data-review-settings', {
      sdms, sdmsResultValidation, sdmsResultApproval, resultValidation, resultApproval,
      skipResultValidation, skipResultApproval, noSelfReview, multiTab,
      suboutResultValidation, suboutResultApproval, electronicSignature, dataPackage,
      fieldDataValidation, fieldDataApproval, manualReagentEntrySdms, sampleReceiptNotification,
      suboutTracking, reporting,
      multipleTabSupport, selfSubmit, qcTemperature, qcHumidity, qcScanQrCode, applyRoundingOnSave,
      prepTemperature, prepHumidity, prepScanQrCode, analysisListView, invoiceSeqCode: invoiceSeqCode.trim() || null,
      // Persist the current step-mapping captions (stored-as-loaded, or recomputed if a
      // toggle was changed) — legacy sets them on the object and saves them too.
      sdmsValidateLabel: sdmsValidateLabel.trim() || null, sdmsApproveLabel: sdmsApproveLabel.trim() || null,
      limsValidateLabel: limsValidateLabel.trim() || null, limsApproveLabel: limsApproveLabel.trim() || null,
    });
  };

  /** Toolbar Save — the shared view-then-edit save (confirm → audited batch →
   *  "Saved successfully." → back to read-only). */
  const handleSave = () => {
    setSaving(true);
    void gate.save({
      formName: 'App Config',
      successMessage: 'Default settings saved successfully.',
      save: persistAll,
      reload: load,
      onError: (e: unknown) => {
        const status = (e as { response?: { status?: number } })?.response?.status;
        notify.error(status === 403
          ? 'Administrator / service access required to change default settings.'
          : 'Save failed. Please try again.');
      },
    }).finally(() => setSaving(false));
  };

  /** Cancel — revert every editable value (incl. the live loader/progress
   *  preview in the shared store) back to the pre-edit snapshot. */
  const revert = () => {
    const s = snapshotRef.current; if (!s) return;
    setLoginModeState(s.loginMode);
    setSdmsRollbackUi(s.rollbackUi);
    setLoaderSymbol(s.symbol as (typeof LOADER_SYMBOLS)[number]);
    setLoaderColor(s.color);
    setProgressStyle(s.progressStyle as (typeof PROGRESS_BAR_STYLES)[number]);
    setProgressColor(s.progressColor);
    setSignEnabled(s.signEnabled);
    setApprovalRequired(s.approvalRequired);
    setSecondFactor(s.secondFactor);
    setSdmsState(s.sdms);
    setSdmsResultValidationState(s.sdmsResultValidation);
    setSdmsResultApprovalState(s.sdmsResultApproval);
    setResultValidationState(s.resultValidation);
    setResultApprovalState(s.resultApproval);
    setSkipResultValidationState(s.skipResultValidation);
    setSkipResultApprovalState(s.skipResultApproval);
    setNoSelfReviewState(s.noSelfReview);
    setMultiTabState(s.multiTab);
    setSuboutResultValidationState(s.suboutResultValidation);
    setSuboutResultApprovalState(s.suboutResultApproval);
    setElectronicSignatureState(s.electronicSignature);
    setDataPackageState(s.dataPackage);
    setFieldDataValidationState(s.fieldDataValidation);
    setFieldDataApprovalState(s.fieldDataApproval);
    setManualReagentEntrySdmsState(s.manualReagentEntrySdms);
    setSampleReceiptNotificationState(s.sampleReceiptNotification);
    setSuboutTrackingState(s.suboutTracking);
    setReportingState(s.reporting);
    setMultipleTabSupportState(s.multipleTabSupport);
    setSelfSubmitState(s.selfSubmit);
    setQcTemperatureState(s.qcTemperature);
    setQcHumidityState(s.qcHumidity);
    setQcScanQrCodeState(s.qcScanQrCode);
    setApplyRoundingOnSaveState(s.applyRoundingOnSave);
    setPrepTemperatureState(s.prepTemperature);
    setPrepHumidityState(s.prepHumidity);
    setPrepScanQrCodeState(s.prepScanQrCode);
    setAnalysisListViewState(s.analysisListView);
    setInvoiceSeqCodeState(s.invoiceSeqCode);
    setSdmsValidateLabelState(s.sdmsValidateLabel);
    setSdmsApproveLabelState(s.sdmsApproveLabel);
    setLimsValidateLabelState(s.limsValidateLabel);
    setLimsApproveLabelState(s.limsApproveLabel);
  };
  const handleCancel = () => gate.cancel(revert);

  return (
    <div className="bl-page">
      <div className="bl-top">
        {/* Breadcrumb + the (empty) FormTitle on ONE row (owner request 2026-10-14).
            FormTitle stays MOUNTED so it keeps publishing the load-time + Audit chips
            to the toolbar via FormMetaContext, but renders no visible content here, so
            there is no empty strip above the toolbar. The Service badge moved DOWN into
            the toolbar row (below). padding-bottom 0 so the toolbar sits just under the
            breadcrumb. */}
        <div className="dp-page-hdr" style={{ padding: '14px 0 0' }}>
          <div className="dp-breadcrumb">
            <span>Home</span>
            <i className="ti ti-chevron-right" />
            <span>Settings</span>
            <i className="ti ti-chevron-right" />
            <span className="dp-bc-active">Default Settings</span>
            <FormTitle pageId="defaultsettings" auditFormName="App Config" loadMs={loadMs} loading={loading} />
          </div>
        </div>
      </div>

      {/* ── Grid area — the toolbar sits ON the grey page (BO UI Standard v2:
          same layout as ModuleListView / Regulatory Compliance); only the card
          below is white and holds the form content. ─────────────────────── */}
      <div className="bl-grid-area">
        {/* Standard action bar (§18.6): view-then-Edit (shared EditGateActions —
            ✎ Edit → 💾 Save / ↩︎ Cancel) · Refresh.  The page is READ-ONLY until
            Edit; Save persists everything in one audited batch.  Audit is the
            config-gated chip on the FormTitle above (view-only).  This is a
            Class F config panel (BO UI Standard §4) — NO New/Edit/Delete/Export
            and no grid footer; there are no rows to export. */}
        {/* Toolbar row: actions on the LEFT, the green Service badge pushed to the far
            RIGHT (owner request 2026-10-14 — moved out of the empty strip above into
            this row). space-between keeps AppToolbar content-width on the left while the
            badge sits at the right end; wraps below on narrow widths. */}
        <div style={{ margin: '0 0 8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <AppToolbar
            left={
              <EditGateActions
                gate={gate}
                saving={saving}
                disabled={loading}
                onSave={handleSave}
                onCancel={handleCancel}
              />
            }
            onRefresh={() => void load()}
            refreshing={loading}
            refreshDisabled={gate.editing}
          />
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            fontSize: 11, fontWeight: 600, color: 'var(--ai-pill-ok-fg)',
            background: 'var(--ai-pill-ok-bg)', border: '1px solid var(--ai-pill-ok-fg)',
            padding: '3px 9px', borderRadius: 12,
          }}>
            <i className="ti ti-shield-check" style={{ fontSize: 12 }} />
            Service
          </span>
        </div>

        <div className="bl-card bl-card-flex">
          <div style={{
            flex: 1, minHeight: 0, overflowY: 'auto',
            padding: '18px 28px 32px',
          }}>
            <div style={{ maxWidth: 980 }}>

              {/* 1 — Login Options (interactive; global login mode) */}
              <Row n={1} label="Login Options">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '2px 0' }}>
                  {/* Combo box instead of radios (owner request 2026-10-15, legacy "1. Login"
                      dropdown parity). The two IdP modes stay selectable-looking but disabled. */}
                  <select
                    value={loginMode} disabled={ro}
                    onChange={(e) => pickLoginMode(e.target.value as LoginMode)}
                    style={{ height: 26, fontSize: 13, padding: '0 8px', border: '1px solid var(--ai-line)', borderRadius: 4, width: 360, maxWidth: '100%', boxSizing: 'border-box', background: ro ? '#f6f7f9' : undefined, color: 'var(--ai-ink)' }}
                  >
                    {LOGIN_MODES.map((o) => (
                      <option key={o.value} value={o.value} disabled={o.disabled}>
                        {o.label}{o.disabled ? ' — coming soon' : ''}
                      </option>
                    ))}
                  </select>
                  {(() => {
                    const sel = LOGIN_MODES.find((m) => m.value === loginMode);
                    return sel ? <div style={{ fontSize: 11.5, color: 'var(--ai-muted)' }}>{sel.desc}</div> : null;
                  })()}
                  {loginMode === 'mfa' && (
                    <div style={{
                      fontSize: 11.5, color: 'var(--ai-amber)', background: 'var(--ai-amber-bg)',
                      border: '1px solid var(--ai-required-line)', borderRadius: 5, padding: '5px 9px', maxWidth: 560,
                    }}>
                      <i className="ti ti-info-circle" style={{ marginRight: 5 }} />
                      MFA is Alpaca Intelligence-only — ALPACA keeps its previous login mode
                      (the legacy login type is not changed for MFA).
                    </div>
                  )}
                </div>
              </Row>
              <Row n={2} label="Subout Result Validation"><YesNo value={suboutResultValidation} disabled={ro} onPick={pickSuboutResultValidation} /></Row>
              <Row n={3} label="Subout Result Approval"><YesNo value={suboutResultApproval} disabled={ro || !suboutResultValidation} onPick={pickSuboutResultApproval} /></Row>
              <Row n={4} label="Result Entry Express"><Box placeholder="Analysis" /></Row>

              {/* 5-9 + Skip 8/9 — Data Review workflow config (interactive; ALPACA
                  parity — see IAppConfigReader.SetDataReviewSettingsAsync). Same
                  dbo.ResultEntryDefaultSettings row the SDMS batch
                  validate/approve status-progression logic
                  (AnalysisWriter.ApplySdmsSampleStatusProgressionAsync) reads —
                  a Save here takes effect for both ALPACA and Alpaca
                  Intelligence immediately. */}
              <Row n={5} label="SDMS">
                <YesNo value={sdms} disabled={ro} onPick={pickSdms} />
                <SkipBox label="Skip 8" checked={skipResultValidation} disabled={ro || !sdms} onPick={pickSkipResultValidation} />
                <SkipBox label="Skip 9" checked={skipResultApproval} disabled={ro || !sdms} onPick={pickSkipResultApproval} />
              </Row>

              <Row n={6} label="SDMS Result Validation">
                <YesNo value={sdmsResultValidation} disabled={ro || !sdms} onPick={pickSdmsResultValidation} />
                {/* Multi-Tab rides the SDMS Result Validation row on the right (owner
                    2026-09-16): Yes → the form's split category tabs (Sample Results /
                    QC Sample Results / Surrogate / Internal Standard); No (default) →
                    one "Batch Results" tab. Only meaningful when Validation is on, so
                    it disables with it. AI-only (AI_AppSettings 'SdmsMultiTab'). */}
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginLeft: 8 }}>
                  <span style={{ color: 'var(--ai-ink)', fontSize: 13, fontWeight: 500 }}>Multi-Tab:</span>
                  <YesNo value={multiTab} disabled={ro || !sdmsResultValidation} onPick={pickMultiTab} />
                </span>
                {/* Read-only step-mapping caption beside #6-#9 (legacy lblSDMSValidate/
                    Approve, lblLIMSValidate/Approve) — shows the STORED value, recomputed
                    only when a toggle changes (owner report 2026-10-15, legacy parity). */}
                <CaptionLabel value={sdmsValidateLabel} />
              </Row>
              <Row n={7} label="SDMS Result Approval">
                <YesNo value={sdmsResultApproval} disabled={ro || !sdms || !sdmsResultValidation} onPick={pickSdmsResultApproval} />
                <CaptionLabel value={sdmsApproveLabel} />
              </Row>
              <Row n={8} label="Result Validation">
                <YesNo value={resultValidation} disabled={ro} onPick={pickResultValidation} />
                <CaptionLabel value={limsValidateLabel} />
              </Row>
              <Row n={9} label="Result Approval">
                <YesNo value={resultApproval} disabled={ro} onPick={pickResultApproval} />
                <CaptionLabel value={limsApproveLabel} />
              </Row>

              <Row n={10} label="Electronic Signature"><OnOff value={electronicSignature} disabled={ro} onPick={pickElectronicSignature} /></Row>
              <Row n={11} label="Quote Validation"><Choice value="No" opts={['Yes', 'No']} /><Box placeholder="Quote Review" /></Row>
              <Row n={12} label="Data Package"><YesNo value={dataPackage} disabled={ro} onPick={pickDataPackage} /></Row>
              <Row n={13} label="No Self-Review">
                <OnOff value={noSelfReview} disabled={ro} onPick={pickNoSelfReview} />
              </Row>
              <Row n={14} label="Field Data Validation"><YesNo value={fieldDataValidation} disabled={ro} onPick={pickFieldDataValidation} /></Row>
              <Row n={15} label="Field Data Approval"><YesNo value={fieldDataApproval} disabled={ro} onPick={pickFieldDataApproval} /></Row>
              <Row n={16} label="Manual Reagent Entry SDMS"><YesNo value={manualReagentEntrySdms} disabled={ro} onPick={pickManualReagentEntrySdms} /></Row>
              <Row n={17} label="Sample Receipt Notification"><YesNo value={sampleReceiptNotification} disabled={ro} onPick={pickSampleReceiptNotification} /></Row>
              <Row n={18} label="Subout Tracking"><YesNo value={suboutTracking} disabled={ro} onPick={pickSuboutTracking} /></Row>
              <Row n={19} label="Reporting"><YesNo value={reporting} disabled={ro} onPick={pickReporting} /></Row>

              {/* Items 20/21 + QC Batch + Prep Batch — remaining legacy ResultEntryDefaultSettings
                  controls (owner request 2026-10-15), wired to the same data-review-settings save. */}
              <Row n={20} label="MultipleTab Support"><OnOff value={multipleTabSupport} disabled={ro} onPick={pickMultipleTabSupport} /></Row>
              <Row n={21} label="Self Submit"><OnOff value={selfSubmit} disabled={ro} onPick={pickSelfSubmit} /></Row>

              <div style={{ padding: '14px 4px 4px', color: 'var(--ai-ink)', fontSize: 13.5, fontWeight: 700 }}>QC Batch:</div>
              <Row label="Temperature"><OnOff value={qcTemperature} disabled={ro} onPick={pickQcTemperature} /></Row>
              <Row label="Humidity"><OnOff value={qcHumidity} disabled={ro} onPick={pickQcHumidity} /></Row>
              <Row label="Scan QR Code"><OnOff value={qcScanQrCode} disabled={ro} onPick={pickQcScanQrCode} /></Row>
              <Row label="Apply Rounding On Save"><OnOff value={applyRoundingOnSave} disabled={ro} onPick={pickApplyRoundingOnSave} /></Row>

              <div style={{ padding: '14px 4px 4px', color: 'var(--ai-ink)', fontSize: 13.5, fontWeight: 700 }}>Prep Batch:</div>
              <Row label="Temperature"><OnOff value={prepTemperature} disabled={ro} onPick={pickPrepTemperature} /></Row>
              <Row label="Humidity"><OnOff value={prepHumidity} disabled={ro} onPick={pickPrepHumidity} /></Row>
              <Row label="Scan QR Code"><OnOff value={prepScanQrCode} disabled={ro} onPick={pickPrepScanQrCode} /></Row>
              <Row label="Analysis List View">
                {([['QCBatch', 'QC Batch ID'], ['JobID', 'Job ID']] as const).map(([v, lab]) => {
                  const active = analysisListView === v;
                  return (
                    <label key={v} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: active ? 700 : 400, color: 'var(--ai-ink)', cursor: ro ? 'default' : 'pointer', fontSize: 13 }}>
                      <input type="radio" checked={active} disabled={ro} onChange={() => pickAnalysisListView(v)} style={{ accentColor: 'var(--brand-accent, var(--ai-orange))' }} />
                      {lab}
                    </label>
                  );
                })}
              </Row>
              <Row label="Invoice Seq Code">
                <input type="text" value={invoiceSeqCode} disabled={ro} maxLength={50}
                  onChange={e => { if (!ro) setInvoiceSeqCodeState(e.target.value); }}
                  style={{ height: 26, fontSize: 13, padding: '0 8px', border: '1px solid var(--ai-line)', borderRadius: 4, width: 240, boxSizing: 'border-box', background: ro ? '#f6f7f9' : undefined }} />
              </Row>

              {/* 22 — Global loading indicator (interactive; client-configurable, §18.6) */}
              <div style={{ marginTop: 22, paddingTop: 18, borderTop: '2px solid var(--ai-line-soft)' }}>
                <div style={{ color: 'var(--ai-ink)', fontSize: 13.5, fontWeight: 500 }}>
                  <span style={{ color: 'var(--ai-muted)', marginRight: 4 }}>22.</span>Loading Indicator:
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--ai-muted)', margin: '4px 0 12px' }}>
                  The animation shown for every loading state app-wide — data retrieve, save, validate,
                  export, report. Applies to all users immediately.
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(104px, 1fr))', gap: 10, maxWidth: 560 }}>
                  {LOADER_SYMBOLS.map((key) => {
                    const active = symbol === key;
                    return (
                      <button
                        key={key} type="button" onClick={() => pickSymbol(key)} disabled={ro}
                        aria-pressed={active}
                        style={{
                          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
                          padding: '16px 8px 10px', cursor: ro ? 'default' : 'pointer',
                          background: active ? 'var(--ai-orange-tint)' : '#fff',
                          border: active ? '2px solid var(--brand-accent, var(--ai-orange))' : '1px solid var(--ai-line)', borderRadius: 6,
                        }}
                      >
                        <span style={{ height: 30, display: 'inline-flex', alignItems: 'center' }}>
                          <LoaderSymbol symbol={key} size={26} />
                        </span>
                        <span style={{ fontSize: 12, fontFamily: 'var(--ai-font-mono)', color: active ? 'var(--ai-orange-hover)' : 'var(--ai-ink)' }}>{key}</span>
                      </button>
                    );
                  })}
                </div>

                <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ai-ink)' }}>Colour:</span>
                  <div style={{ display: 'inline-flex', gap: 8 }}>
                    {PRESET_LOADER_COLORS.map((c) => {
                      const active = c.toLowerCase() === color.toLowerCase();
                      return (
                        <button
                          key={c} type="button" onClick={() => pickColor(c)} disabled={ro}
                          aria-label={`Set loader colour ${c}`} title={c}
                          style={{
                            width: 26, height: 26, borderRadius: '50%', background: c,
                            cursor: ro ? 'default' : 'pointer',
                            border: active ? '2px solid var(--ai-ink)' : '1px solid var(--ai-line)',
                            boxShadow: active ? '0 0 0 2px #fff inset' : 'none',
                          }}
                        />
                      );
                    })}
                  </div>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--ai-muted)' }}>
                    Custom
                    <input
                      type="color"
                      /* token-exempt: <input type=color> value must be a literal hex */
                      value={/^#[0-9a-fA-F]{6}$/.test(color) ? color : '#e8670a'}
                      onChange={(e) => pickColor(e.target.value)} disabled={ro}
                      style={{ width: 34, height: 26, padding: 0, border: '1px solid var(--ai-line)', borderRadius: 4, cursor: 'pointer' }}
                    />
                  </label>
                  <span style={{ fontFamily: 'var(--ai-font-mono)', fontSize: 12, color: 'var(--ai-muted)' }}>{color}</span>
                </div>

                <div style={{ marginTop: 10, fontSize: 11, color: 'var(--ai-muted)' }}>
                  {/* token-exempt: literal default value shown as help text */}
                  Default symbol <code>ring</code>, colour <code>#e8670a</code> — both apply to all users immediately.
                </div>
              </div>

              {/* 21 — Progress bar (interactive; client-configurable, §18.6) */}
              <div style={{ marginTop: 22, paddingTop: 18, borderTop: '2px solid var(--ai-line-soft)' }}>
                <div style={{ color: 'var(--ai-ink)', fontSize: 13.5, fontWeight: 500 }}>
                  <span style={{ color: 'var(--ai-muted)', marginRight: 4 }}>23.</span>Progress Bar:
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--ai-muted)', margin: '4px 0 12px' }}>
                  Shown for determinate progress (exports, report generation) and the top-of-page
                  loading bar — everywhere a progress bar appears. Applies to all users immediately.
                </div>

                {/* Live demo bar — fills 0→100% on a loop so you can SEE the
                    current style (stripes / pulse / glow) + the light→bright colour. */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                  <div style={{ height: 10, width: 300, maxWidth: '100%', background: 'var(--ai-loader-track)', borderRadius: 5, overflow: 'hidden' }}>
                    <div className="ld-pbar-fill" style={{ height: '100%', width: `${demoPct}%`, borderRadius: 5, transition: 'width .08s linear' }} />
                  </div>
                  <span style={{ fontFamily: 'var(--ai-font-mono)', fontSize: 12, color: 'var(--ai-muted)', minWidth: 36 }}>{demoPct}%</span>
                </div>

                {/* Style options */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
                  <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ai-ink)' }}>Style:</span>
                  {PROGRESS_BAR_STYLES.map((st) => {
                    const active = progressStyle === st;
                    return (
                      <button
                        key={st} type="button" onClick={() => pickStyle(st)} disabled={ro}
                        aria-pressed={active}
                        style={{
                          padding: '6px 14px', fontSize: 12.5, fontFamily: 'monospace', borderRadius: 6,
                          cursor: ro ? 'default' : 'pointer',
                          background: active ? 'var(--ai-orange-tint)' : '#fff',
                          border: active ? '2px solid var(--brand-accent, var(--ai-orange))' : '1px solid var(--ai-line)',
                          color: active ? 'var(--ai-orange-hover)' : 'var(--ai-ink)',
                        }}
                      >{st}</button>
                    );
                  })}
                </div>

                {/* Colour */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ai-ink)' }}>Colour:</span>
                  <div style={{ display: 'inline-flex', gap: 8 }}>
                    {PRESET_LOADER_COLORS.map((c) => {
                      const active = c.toLowerCase() === progressColor.toLowerCase();
                      return (
                        <button
                          key={c} type="button" onClick={() => pickProgressColor(c)} disabled={ro}
                          aria-label={`Set progress-bar colour ${c}`} title={c}
                          style={{
                            width: 26, height: 26, borderRadius: '50%', background: c,
                            cursor: ro ? 'default' : 'pointer',
                            border: active ? '2px solid var(--ai-ink)' : '1px solid var(--ai-line)',
                            boxShadow: active ? '0 0 0 2px #fff inset' : 'none',
                          }}
                        />
                      );
                    })}
                  </div>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--ai-muted)' }}>
                    Custom
                    <input
                      type="color"
                      /* token-exempt: <input type=color> value must be a literal hex */
                      value={/^#[0-9a-fA-F]{6}$/.test(progressColor) ? progressColor : '#e8670a'}
                      onChange={(e) => pickProgressColor(e.target.value)} disabled={ro}
                      style={{ width: 34, height: 26, padding: 0, border: '1px solid var(--ai-line)', borderRadius: 4, cursor: 'pointer' }}
                    />
                  </label>
                  <span style={{ fontFamily: 'var(--ai-font-mono)', fontSize: 12, color: 'var(--ai-muted)' }}>{progressColor}</span>
                </div>

                <div style={{ marginTop: 10, fontSize: 11, color: 'var(--ai-muted)' }}>
                  {/* token-exempt: literal default value shown as help text */}
                  Default style <code>solid</code>, colour <code>#e8670a</code> — applies to every progress bar in the app.
                </div>
              </div>

              {/* 22 — Report Signing (interactive; ADR-0009) */}
              <div style={{ marginTop: 22, paddingTop: 18, borderTop: '2px solid var(--ai-line-soft)' }}>
                <div style={{ color: 'var(--ai-ink)', fontSize: 13.5, fontWeight: 500 }}>
                  <span style={{ color: 'var(--ai-muted)', marginRight: 4 }}>22.</span>Report Signing (e-signature):
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--ai-muted)', margin: '4px 0 14px' }}>
                  When enabled, Report Validation / Approval require an electronic signature —
                  password + a second factor + a certification. Applies to all users immediately.
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', columnGap: 24, rowGap: 14, alignItems: 'center', maxWidth: 760 }}>
                  <div style={{ color: 'var(--ai-ink)', fontSize: 13.5, fontWeight: 500 }}>Require e-signature:</div>
                  <OnOff value={signEnabled} disabled={ro}
                    onPick={(v) => {
                      setSignEnabled(v);
                      // E-signature implies a two-signature workflow: turning it on forces
                      // Approval (the 2nd signature) on; it cannot be turned off while e-sign is on.
                      if (v) setApprovalRequired(true);
                    }} />

                  <div style={{ color: 'var(--ai-ink)', fontSize: 13.5, fontWeight: 500 }}>Require Approval (2nd signature):</div>
                  <OnOff value={signEnabled || approvalRequired} disabled={ro || signEnabled}
                    onPick={(v) => setApprovalRequired(v)} />

                  <div style={{ color: 'var(--ai-ink)', fontSize: 13.5, fontWeight: 500 }}>Second factor (besides password):</div>
                  <select value={secondFactor} disabled={ro}
                    onChange={(e) => setSecondFactor(e.target.value)}
                    style={{ width: 240, padding: '6px 10px', border: '1px solid var(--ai-line)', borderRadius: 5, background: '#fff', color: 'var(--ai-ink)', fontSize: 13 }}>
                    <option value="Mfa">Authenticator app (MFA)</option>
                    <option value="Email">One-time code by Email</option>
                    <option value="Sms">One-time code by SMS</option>
                  </select>
                </div>
                <div style={{ marginTop: 12, fontSize: 11, color: 'var(--ai-muted)' }}>
                  Password is always required. {signEnabled
                    ? <>While e-signature is required, <b>Approval is mandatory</b> (two signatures) and cannot be turned off.</>
                    : <>Approval defaults <code>Off</code> (one signature at validation).</>}
                </div>
              </div>

              {/* 23 — SDMS Rollback UI (interactive; client-configurable, §18.6) */}
              <div style={{ marginTop: 22, paddingTop: 18, borderTop: '2px solid var(--ai-line-soft)' }}>
                <div style={{ color: 'var(--ai-ink)', fontSize: 13.5, fontWeight: 500 }}>
                  <span style={{ color: 'var(--ai-muted)', marginRight: 4 }}>23.</span>SDMS Rollback:
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--ai-muted)', margin: '4px 0 12px' }}>
                  The Rollback window on the SDMS Result Validation form. <b>Classic</b> asks only for a
                  reason (today&rsquo;s behaviour); <b>Enhanced</b> adds a Roll Back type
                  (Correction / Re-analysis / Re-prep) with a scoped Sample&nbsp;&times;&nbsp;Parameter selection.
                </div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  {([
                    ['classic', 'Classic', 'Reason-only popup'],
                    ['enhanced', 'Enhanced', 'Typed roll-back window'],
                  ] as Array<['classic' | 'enhanced', string, string]>).map(([val, label, desc]) => {
                    const on = rollbackUi === val;
                    return (
                      <button
                        key={val}
                        type="button"
                        disabled={ro}
                        onClick={() => pickRollbackUi(val)}
                        style={{
                          textAlign: 'left', minWidth: 210, padding: '10px 12px', borderRadius: 8, cursor: ro ? 'default' : 'pointer',
                          border: `1px solid ${on ? 'var(--brand-accent, #e8670a)' : 'var(--ai-line)'}`,
                          background: on ? 'var(--ai-required-bg, #fff3ec)' : '#fff',
                          opacity: ro && !on ? 0.6 : 1,
                        }}
                      >
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ai-ink)' }}>{label}</div>
                        <div style={{ fontSize: 11, color: 'var(--ai-muted)' }}>{desc}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
